import { Globe } from 'lucide-react';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import {
  CardColumn,
  FullHeightCard,
  CardContentWrapper,
  StatusRow,
  HostTitleGroup,
  HeaderIconBox,
  HostNameText,
  StatusBadge,
  StatusBadgeText,
  TimeMetricsGrid,
  TimeMetricCard,
  MetricHeaderGroup,
  MetricLabelText,
  MetricValueGroup,
  MetricValueText,
  MetricUnitText,
  SectionDivider,
  PackagesRow,
  StatItemStart,
  StatItemCenter,
  StatItemEnd,
  StatLabelText,
  StatItemValueText,
  StatItemNumberText,
  ProgressBarContainer,
  HopsProgressBar,
} from '../../styles';
import type { StatusInfo, TimeMetricItem, TracertStats } from '../../types';

interface ResultsCardProps {
  host: string;
  statusInfo: StatusInfo;
  timeMetrics: TimeMetricItem[];
  stats: TracertStats;
  isComplete: boolean;
  isError: boolean;
  hopsPercentage: string | null;
}

// Displays the results of the traceroute operation
export function ResultsCard({
  host,
  statusInfo,
  timeMetrics,
  stats,
  isComplete,
  isError,
  hopsPercentage,
}: ResultsCardProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <CardColumn>
      <FullHeightCard>
        <CardContentWrapper>
          {/* Status row */}
          <StatusRow>
            <HostTitleGroup>
              <HeaderIconBox>
                <Globe size={18} />
              </HeaderIconBox>
              <HostNameText variant="h4">{host || '—'}</HostNameText>
            </HostTitleGroup>
            <StatusBadge $color={statusInfo.color}>
              <statusInfo.Icon
                size={14}
                strokeWidth={2.5}
                color={theme.palette.white['01']}
              />
              <StatusBadgeText variant="caption">
                {statusInfo.label}
              </StatusBadgeText>
            </StatusBadge>
          </StatusRow>

          {/* Time metrics */}
          <TimeMetricsGrid>
            {timeMetrics.map((m) => (
              <TimeMetricCard key={m.id}>
                <MetricHeaderGroup>
                  <m.icon
                    size={14}
                    color={
                      m.id === 'maxHopCount'
                        ? theme.palette.primary.main
                        : m.color
                    }
                  />
                  <MetricLabelText>{m.label}</MetricLabelText>
                </MetricHeaderGroup>
                <MetricValueGroup>
                  <MetricValueText
                    variant="h2"
                    $color={
                      m.value !== 'N/A' ? m.color : theme.palette.text.disabled
                    }
                  >
                    {m.value !== 'N/A' ? m.value : '—'}
                  </MetricValueText>
                  {m.unit && m.value !== 'N/A' && (
                    <MetricUnitText variant="caption">{m.unit}</MetricUnitText>
                  )}
                </MetricValueGroup>
              </TimeMetricCard>
            ))}
          </TimeMetricsGrid>

          <SectionDivider />

          {/* Secondary stats */}
          <PackagesRow>
            <StatItemStart>
              <StatLabelText variant="caption">
                {t('tools.traceroute.hopsRatio')}
              </StatLabelText>
              <MetricValueGroup>
                <StatItemValueText
                  variant="h3"
                  $color={
                    isComplete
                      ? theme.palette.status.online
                      : theme.palette.text.primary
                  }
                >
                  {stats.hopsCount !== 'N/A' ? stats.hopsCount : '—'}
                </StatItemValueText>
                {stats.maxHopCount !== 'N/A' && (
                  <StatLabelText variant="caption">
                    / {stats.maxHopCount}{' '}
                    {hopsPercentage !== null ? `(${hopsPercentage}%)` : ''}
                  </StatLabelText>
                )}
              </MetricValueGroup>
            </StatItemStart>

            <StatItemCenter>
              <StatLabelText variant="caption">
                {t('tools.traceroute.numberTries')}
              </StatLabelText>
              <StatItemNumberText variant="h3">
                {stats.numberTries !== 'N/A' ? stats.numberTries : '—'}
              </StatItemNumberText>
            </StatItemCenter>

            <StatItemEnd>
              <StatLabelText variant="caption">
                {t('tools.traceroute.routeStatus')}
              </StatLabelText>
              <Typography
                color={statusInfo.color}
                variant="h3"
                fontWeight={700}
              >
                {isComplete
                  ? t('tools.traceroute.completed')
                  : isError
                    ? t('tools.traceroute.failed')
                    : '—'}
              </Typography>
            </StatItemEnd>
          </PackagesRow>

          <ProgressBarContainer>
            <HopsProgressBar
              variant="determinate"
              value={
                isComplete
                  ? hopsPercentage
                    ? parseFloat(hopsPercentage)
                    : 100
                  : 0
              }
              $isComplete={isComplete}
            />
          </ProgressBarContainer>
        </CardContentWrapper>
      </FullHeightCard>
    </CardColumn>
  );
}

export default ResultsCard;
